import {
    Normalize3, Mul3_1, Add3_3, MagSq3, Dot3, Cross3, Sub3_3,
} from '../math';
import {
    isHydrogen, isPhosphorus, isOxygen, isSulfur, isHalogen,
    atomHasEquivalentTerminals, isAromaticAtom,
} from '../util/chem_utils';
import { getAtomRadius } from '../util/clash_detection';
import { pointDistanceSquared } from '../util/atom_distance_utils';
import { getFullResidueId } from '../util/mol_info_utils';
import { filterAvailableLinkTypes } from './linker_types';

/**
 * @typedef {import('BMapsModel').Atom} Atom
 * @typedef {import('BMapsModel').DecodedFragment} DecodedFragment
 */

/**
 * Find fragment atoms that can form bonds with compound atoms based on distance and angle
 * constraints. Supports link types: Bond (direct), methylene (one-carbon linker),
 * ethane (two-carbon linker), acetylene (triple-bond linker), Near (proximity only),
 * plus heteroatom linkers (amine, ether, ketone, etc.), X-methylene linkers,
 * and ring linkers (piperazine, triazoles, oxadiazole, benzene isomers).
 * @param {{
 *  findType: string,
 *  bondVectorAtomPairs: [Atom, Atom|null][],
 *  searchRadius?: number,
 *  searchTolerance?: number,
 * }} params - Search parameters including bond vector atom pairs and spatial constraints
 * @param {DecodedFragment[]} fragments - Candidate fragment poses to evaluate
 * @returns {{ startAtom: Atom, startTerminal: Atom|null, endAtom: Atom,
 *   endTerminal: Atom|null, findType: string }[]}
 */
export function bondableSearchResults(params, fragments) {
    // constants
    const maxUserRadius = 0;
    const maxFragmentRadius = 0;

    // Inline function for squaring a number
    const square = (num) => num ** 2;

    // Inline function for calculating the cosine of an angle in degrees
    const cosdeg = (deg) => Math.cos((deg * Math.PI) / 180);

    /** @type {(rminsq: number, rmaxsq: number, cosmin: number, cosmax: number) => object} */
    const newParam = (rminsq, rmaxsq, cosmin, cosmax) => ({
        rminsq, rmaxsq, cosmin, cosmax,
    });

    /**
     * @param {Atom} startAtom @param {Atom|null} startTerminal
     * @param {Atom} endAtom @param {Atom|null} endTerminal
     * @param {string} findType
     */
    const newModSpecs = (startAtom, startTerminal, endAtom, endTerminal, findType) => ({
        startAtom, startTerminal, endAtom, endTerminal, findType,
    });

    // dist min, dist max, cos min, cos max.
    const typeParamsMap = {
        Near: newParam(square(0.0), square(5.0), 0.0, 0.0),
        Bond: newParam(square(1.5 - 0.6), square(1.5 + 0.6), cosdeg(30), cosdeg(0)),
        // methylene link bonds can bend to accommodate distance variation (methane link?).
        methylene: newParam(square(2.6 - 0.8), square(2.6 + 0.8), cosdeg(35 + 20), cosdeg(35 - 20)),
        // ethylene has two configurations: cis (3.0), trans (3.8).
        // J3 thinks these are metabolically unstable, but consider PEG, plants, etc.
        // ethylene_cis:
        // newParam(square(3.0 - 0.5), square(3.0 + 0.5), cosdeg(54 + 10), cosdeg(54 - 10)),
        // ethylene_trans:
        // newParam(square(3.8 - 0.5), square(3.8 + 0.5), cosdeg(20), cosdeg(0)),
        ethane: newParam(square(3.0 - 0.5), square(3.87 + 0.5), cosdeg(30), cosdeg(0)),
        acetylene: newParam(square(4.13 - 0.5), square(4.13 + 0.5), cosdeg(20), cosdeg(0)),
        // These should be probably be done with fragment growing? [not currently supported.]
        amideBond: newParam(square(3.3), square(4.1), cosdeg(31), cosdeg(5)),
        amideSyn: newParam(square(2.5), square(3.1), cosdeg(65), cosdeg(52)),
        // --- 1-atom linkers ---
        amine: newParam(square(0.95), square(2.35), cosdeg(52.2), cosdeg(22.2)),
        ketone: newParam(square(1.16), square(2.56), cosdeg(45.0), cosdeg(15.0)),
        ether: newParam(square(0.81), square(2.21), cosdeg(52.5), cosdeg(22.5)),
        thioether: newParam(square(1.50), square(2.90), cosdeg(50.3), cosdeg(20.3)),
        sulfoxide: newParam(square(1.47), square(2.87), cosdeg(52.0), cosdeg(22.0)),
        sulfone: newParam(square(1.50), square(2.90), cosdeg(50.3), cosdeg(20.3)),
        // --- 2-atom / multi-atom chain linkers ---
        amide: newParam(square(2.41), square(3.81), cosdeg(39.1), cosdeg(9.1)),
        carbamate: newParam(square(3.43), square(4.83), cosdeg(47.5), cosdeg(17.5)),
        urea: newParam(square(3.62), square(5.02), cosdeg(42.8), cosdeg(12.8)),
        thiourea: newParam(square(3.63), square(5.03), cosdeg(44.5), cosdeg(14.5)),
        sulfonamide: newParam(square(2.49), square(3.89), cosdeg(47.4), cosdeg(17.4)),
        // --- X-methylene linkers (rotatable bond like methylene) ---
        'amide-methylene': newParam(square(2.74), square(4.14), cosdeg(57.3), cosdeg(27.3)),
        'amine-methylene': newParam(square(2.15), square(3.55), cosdeg(45.2), cosdeg(15.2)),
        'ether-methylene': newParam(square(2.03), square(3.43), cosdeg(44.8), cosdeg(14.8)),
        'ketone-methylene': newParam(square(2.36), square(3.76), cosdeg(42.1), cosdeg(12.1)),
        'thioether-methylene': newParam(square(2.64), square(4.04), cosdeg(46.8), cosdeg(16.8)),
        // --- ring linkers (tighter tolerances) ---
        piperazine: newParam(square(3.56), square(4.56), cosdeg(60.7), cosdeg(40.7)),
        'triazole-123-14diG': newParam(square(3.68), square(4.68), cosdeg(27.5), cosdeg(7.5)),
        'triazole-124-13diG': newParam(square(3.57), square(4.57), cosdeg(28.0), cosdeg(8.0)),
        'triazole-124-35diG': newParam(square(3.62), square(4.68), cosdeg(27.4), cosdeg(7.4)),
        'oxadiazole-124-35diG': newParam(square(3.58), square(4.58), cosdeg(25.5), cosdeg(5.5)),
        'benzene-14diG': newParam(square(4.53), square(5.53), cosdeg(10.2), cosdeg(0)),
        'benzene-13diG': newParam(square(3.85), square(4.85), cosdeg(40.1), cosdeg(20.1)),
        'benzene-12diG': newParam(square(2.01), square(3.01), cosdeg(70.1), cosdeg(50.1)),
        // --- Reverse variants (same search params as forward) ---
        'amide-rev': newParam(square(2.41), square(3.81), cosdeg(39.1), cosdeg(9.1)),
        'amideBond-rev': newParam(square(3.3), square(4.1), cosdeg(31), cosdeg(5)),
        'amideSyn-rev': newParam(square(2.5), square(3.1), cosdeg(65), cosdeg(52)),
        'sulfonamide-rev': newParam(square(2.49), square(3.89), cosdeg(47.4), cosdeg(17.4)),
        'amine-methylene-rev': newParam(square(2.15), square(3.55), cosdeg(45.2), cosdeg(15.2)),
        'ether-methylene-rev': newParam(square(2.03), square(3.43), cosdeg(44.8), cosdeg(14.8)),
        'thioether-methylene-rev': newParam(square(2.64), square(4.04), cosdeg(46.8), cosdeg(16.8)),
        'ketone-methylene-rev': newParam(square(2.36), square(3.76), cosdeg(42.1), cosdeg(12.1)),
        'carbamate-rev': newParam(square(3.43), square(4.83), cosdeg(47.5), cosdeg(17.5)),
        'amide-methylene-rev': newParam(square(2.74), square(4.14), cosdeg(57.3), cosdeg(27.3)),
        'triazole-123-14diG-rev': newParam(square(3.68), square(4.68), cosdeg(27.5), cosdeg(7.5)),
        'triazole-124-13diG-rev': newParam(square(3.57), square(4.57), cosdeg(28.0), cosdeg(8.0)),
        'triazole-124-35diG-rev': newParam(square(3.62), square(4.68), cosdeg(27.4), cosdeg(7.4)),
        'oxadiazole-124-35diG-rev': newParam(square(3.58), square(4.58), cosdeg(25.5), cosdeg(5.5)),
    };

    // For each bond atom pair (bond vector), search nearby fragments (as determined by
    // the fragment grid) for bondable atoms satisfying the distance and angle constraints
    // for a given linkage type (mtype). Note, it is assumed that fragments have already
    // been filtered for being in a relevant energy range. Note that the cos of the angles
    // change in magnitude opposite to the angles (smaller angles are bigger cosines, and
    // the inverse).
    const bondableFragResults = [];
    const report = false;
    const mtypeIsNear = params.findType === 'Near';
    const maxRadius = params.searchRadius ? params.searchRadius : maxUserRadius;
    const maxRadiusSq = square(maxRadius);
    const searchTol = params.searchTolerance || 1;
    const searchTolSq = square(searchTol);
    //--- ethylene links not yet supported below (particularly the cis configuration), and
    //--- assembly of ethylene not implemented yet in bfd-server.elp (addLinkAtoms).
    /* 'ethylene_cis', 'ethylene_trans', */
    // The full set of implemented linkers. Search params for every one of
    // these are preserved in typeParamsMap above; filterAvailableLinkTypes
    // restricts the dataservice path to the currently-approved subset (old
    // types plus the approved new ones) without removing any code. See
    // linker_types.js to change which new linkers are available.
    const linkTypes = mtypeIsNear
        ? ['Near']
        : filterAvailableLinkTypes([
            'Bond', 'methylene', 'ethane', 'acetylene',
            'amideBond', 'amideSyn',
            'amine', 'ketone', 'ether', 'thioether', 'sulfoxide', 'sulfone',
            'amide', 'carbamate', 'urea', 'thiourea', 'sulfonamide',
            'amide-methylene', 'amine-methylene', 'ether-methylene',
            'ketone-methylene', 'thioether-methylene',
            'piperazine',
            'triazole-123-14diG', 'triazole-124-13diG', 'triazole-124-35diG',
            'oxadiazole-124-35diG',
            'benzene-14diG', 'benzene-13diG', 'benzene-12diG',
            // Reverse variants for asymmetric linkers
            'amide-rev', 'amideBond-rev', 'amideSyn-rev',
            'sulfonamide-rev',
            'amine-methylene-rev', 'ether-methylene-rev', 'thioether-methylene-rev',
            'ketone-methylene-rev',
            'carbamate-rev', 'amide-methylene-rev',
            'triazole-123-14diG-rev', 'triazole-124-13diG-rev',
            'triazole-124-35diG-rev', 'oxadiazole-124-35diG-rev',
        ]);

    // Copy bondVectorAtomPairs as it gets reused.
    for (const atomPair of params.bondVectorAtomPairs) {
        const [startAtom, startTerminal] = atomPair;

        if (startAtom === null) {
            console.log('Search requires a non-null start atom.');
            return bondableFragResults;
        }
        if (!mtypeIsNear && startTerminal === null) {
            console.log('Search types require a non-null terminal atom.');
            return bondableFragResults;
        }
        const atomPos = startAtom.getPosition();
        const termPos = startTerminal?.getPosition();

        for (const ltype of linkTypes) {
            let existingResultsSize = bondableFragResults.length;
            const methyleneLikeTypes = new Set([
                'methylene', 'amine-methylene', 'ether-methylene',
                'ketone-methylene', 'thioether-methylene', 'amide-methylene',
                'amine-methylene-rev', 'ether-methylene-rev',
                'ketone-methylene-rev', 'thioether-methylene-rev', 'amide-methylene-rev',
            ]);
            const isLTypeMethylene = methyleneLikeTypes.has(ltype);
            const isLTypeEthane = ltype === 'ethane';
            const entry = typeParamsMap[ltype]; // numeric params for search by link type
            if (!entry) { // invalid type? Error report?
                console.warn(`Unsupported link type ${ltype}.`);
                return [];
            }
            const linkParams = entry; // SearchParams: rminsq, rmaxsq, cosmin, cosmax.
            if (report) {
                console.log(`Doing ${ltype} search, is ${isLTypeMethylene}.`);
            }
            const searchMinDistSq = linkParams.rminsq; // tolerance?
            const searchMaxDistSq = searchTolSq * linkParams.rmaxsq;
            const searchCosMin = linkParams.cosmin;
            const searchCosMax = linkParams.cosmax; // tolerance?
            const searchRadius = Math.max(
                maxRadius, maxFragmentRadius + Math.sqrt(searchMaxDistSq) + 0.5
            );
            const candidateFragments = fragments;
            // grid.CollectNeighbors(candidateFragments, atomPos, searchRadius);
            if (report) console.log(`search radius ${searchRadius.toFixed(3)}`);
            let atomAtomVec = [0, 0, 0];
            let bondVec = [0, 0, 0];
            let bondCross = [0, 0, 0];
            let catomPos = [0, 0, 0];
            if (!mtypeIsNear && termPos !== null) {
                bondVec = Sub3_3(termPos, atomPos);
                bondVec = Normalize3(bondVec); // make a unit vector
            }
            if (isLTypeEthane) {
                const cvec = Mul3_1(bondVec, 1.5); // 1.5 is carbon-carbon bond length
                catomPos = Add3_3(atomPos, cvec); // Position of one of the carbon linkers
            }

            for (const [jf, frag] of candidateFragments.entries()) {
                if (mtypeIsNear) {
                    // Distance is sufficient
                    const atomFragVec = Sub3_3(frag.translation, atomPos);
                    const distsq = MagSq3(atomFragVec);
                    if ((maxRadius !== 0.0 && distsq > maxRadiusSq)
                        || (maxRadius === 0.0 && distsq > searchMaxDistSq)) {
                        // min radius ignored for Near
                        continue;
                    }
                    const modSpecs = newModSpecs(
                        startAtom, startTerminal, frag.atoms[0], null, 'Near'
                    );
                    bondableFragResults.push(modSpecs);
                    continue;
                }

                let fragVec = [0, 0, 0];
                let fragCross = [0, 0, 0];
                const nAtoms = frag.atoms.length;
                for (let i=0; i<nAtoms; i++) {
                    //--- Avoid finding bond vector pairs?
                    const fatom = frag.atoms[i];
                    // Check for atoms that can't be linked to.
                    atomAtomVec = Sub3_3(fatom.getPosition(), atomPos);
                    let distsq = MagSq3(atomAtomVec);
                    if (distsq < searchMinDistSq || distsq > searchMaxDistSq) continue;
                    if (isHydrogen(fatom) || isPhosphorus(fatom) || isHalogen(fatom)
                        || ((isOxygen(fatom) || isSulfur(fatom)) && fatom.hasDoubleBond())) {
                        continue;
                    }
                    const excludeUnsafeNN = false;
                    if (excludeUnsafeNN && linkCreatesUnsafeNNBond(ltype, startAtom, fatom)) {
                        // Block alert-class N-N substructures (acyclic dialkyl/alkyl-aryl
                        // hydrazines, N-aminoamines, semicarbazides). Hoisted here since
                        // the result is constant across all bond-vector pairs from this fatom.
                        // TODO: make this a user selection someday.
                        continue;
                    }
                    // make a unit vector between the query atom and frag atom
                    atomAtomVec = Normalize3(atomAtomVec);
                    // If < 0, means angle is > 90 deg.
                    const cosangle = Dot3(atomAtomVec, bondVec);
                    if (cosangle < searchCosMin || cosangle > searchCosMax) continue;
                    if (isLTypeMethylene) {
                        bondCross = Cross3(atomAtomVec, bondVec);
                        bondCross = Normalize3(bondCross);
                    }
                    // Reverse the vector to point back to the start atom
                    atomAtomVec = Mul3_1(atomAtomVec, -1.0);
                    // Ring ordinals are pre-computed by the server and set on
                    // atoms during decode (atom.ringOrdinals), so no client-side
                    // ring detection is needed.
                    //--- does this have to be done every time or can atom names be
                    //--- determined once for a particular type of fragment, then the
                    //--- particular atoms looked up? Address in findFragBondVectorAtomPairs.
                    //--- But the explicit atoms from each fragment are needed.
                    //--- Could look up by name. But is this pretty fast anyway?
                    const fragAtomBondPairs = findFragBondVectorAtomPairs(fatom, frag);

                    // if (report) ReportStatus("%zu bondable pairs", fragAtomBondPairs.size());
                    for (const fabp of fragAtomBondPairs) {
                        const [fragAtom, fragTerminal] = fabp;

                        if (report) console.log(`Considering ${frag.name}: ${fragAtom.atom}->${fragTerminal.atom}`);
                        const fragAtomPos = fragAtom.getPosition();
                        const fragTermPos = fragTerminal.getPosition();
                        fragVec = Sub3_3(fragTermPos, fragAtomPos);
                        fragVec = Normalize3(fragVec);
                        const ocosangle = Dot3(atomAtomVec, fragVec);
                        // Be somewhat more forgiving of the cosmin for the reverse bond
                        // vector. Note, smaller cosmin is a larger angle.
                        if (ocosangle < searchCosMin*0.75 || ocosangle > searchCosMax) continue;
                        if (isLTypeMethylene) {
                            // The cross product is the perpendicular to the plane defined by the
                            // atom-atom vector and the bond vector.
                            fragCross = Cross3(atomAtomVec, fragVec);
                            fragCross = Normalize3(fragCross);
                            const crossCosangle = Dot3(bondCross, fragCross);
                            // If perfectly aligned, bondCross and fragCross will be in
                            // opposite directions (-1 cosine).
                            const maxRotAngle = 180-45;
                            if (crossCosangle > cosdeg(maxRotAngle)) continue;
                        } else if (isLTypeEthane) {
                            // 1. Check distance between the two carbon linker atoms.
                            // 2. Check angles between the C-C vector and the start-C vectors.
                            let c1pos = [];
                            let c1vec = [];
                            let ccvec = [];
                            let mbondVec = [];
                            let mfragVec = [];
                            c1vec = Mul3_1(fragVec, 1.5);
                            c1pos = Add3_3(fragAtomPos, c1vec);
                            ccvec = Sub3_3(c1pos, catomPos);
                            distsq = MagSq3(ccvec);
                            if (distsq < square(1.2) || distsq > square(1.8)) continue;
                            ccvec = Normalize3(ccvec);
                            mbondVec = Mul3_1(bondVec, -1.0);
                            const cccos = Dot3(mbondVec, ccvec);
                            ccvec = Mul3_1(ccvec, -1.0);
                            mfragVec = Mul3_1(fragVec, -1.0);
                            const cfcos = Dot3(mfragVec, ccvec);
                            const cosmin = searchTol*cosdeg(111.5+20.0);
                            const cosmax = cosdeg(111.5-20.0);
                            if (
                                cccos < cosmin || cccos > cosmax || cfcos < cosmin || cfcos > cosmax
                            ) continue;
                        }
                        if (report) console.log(`Found ${frag.name} link ${ltype}.`);
                        const modSpecs = newModSpecs(
                            startAtom, startTerminal, fragAtom, fragTerminal, ltype
                        );
                        bondableFragResults.push(modSpecs);
                        // The remaining (fatom, terminal) pairs differ only in which
                        // rotationally-equivalent H/halogen was chosen, so they would
                        // produce duplicate results in the assembled compound.
                        if (atomHasEquivalentTerminals(fatom)) break;
                    }
                }
            }
            console.log(`Examined ${candidateFragments.length} candidates for link type ${ltype}, ${bondableFragResults.length - existingResultsSize} bondable for bond vector ${startAtom.atom} to ${startTerminal === null ? 'n/a' : startTerminal.atom}.`);
            existingResultsSize = bondableFragResults.length;
        }
    }

    // Sort results by findType, then fragment name, then interaction energy (enSolute).
    bondableFragResults.sort((a, b) => {
        const typeComp = a.findType.localeCompare(b.findType);
        if (typeComp !== 0) return typeComp;
        const fragA = a.endAtom?.fragment;
        const fragB = b.endAtom?.fragment;
        if (!fragA || !fragB) return 0;
        const nameComp = (fragA.parentName || '').localeCompare(fragB.parentName || '');
        if (nameComp !== 0) return nameComp;
        // Tiebreak by enSolute, which is valid for both classic and gen2 poses. This is only a
        // stable default order; LigandMod re-sorts the displayed rows/poses by the active primary
        // score, so the exact metric here isn't load-bearing.
        const aV = fragA.enSolute;
        const bV = fragB.enSolute;
        if (aV == null) return bV == null ? 0 : 1;
        if (bV == null) return -1;
        return aV - bV;
    });

    return bondableFragResults;
}

// Linker classes whose attachment chemistry would form an alert-class N-N
// bond if a non-aromatic N is on the indicated side(s). Tolerated classes
// (sulfonamide, carbamate, amide*, amide-methylene*) and aromatic ring
// linkers (triazoles, oxadiazoles, etc.) are intentionally absent — those
// either land in safer hazard tiers or in the always-allowed aromatic 5/6
// heterocycle class.
const NN_ALERT_BOTH_SIDES = new Set([
    'amine', 'urea', 'thiourea', 'piperazine',
]);
const NN_ALERT_BASE1_SIDE = new Set(['amine-methylene']);
const NN_ALERT_BASE2_SIDE = new Set(['amine-methylene-rev']);

/** @param {Atom} atom */
function isUnsafeN(atom) {
    return atom?.elem === 'N' && !isAromaticAtom(atom);
}

/**
 * Returns true when joining base1 and base2 with the given linker would
 * produce a headline genotox N-N substructure (acyclic alkyl/alkyl-aryl
 * hydrazine, N-aminoamine, semicarbazide). Aromatic N on either side
 * disables the block — the result lands in the safe aromatic heterocycle class.
 * @param {string} linkType
 * @param {Atom} base1 The startAtom (source compound side).
 * @param {Atom} base2 The fragment-side attachment atom.
 * @returns {boolean}
 */
function linkCreatesUnsafeNNBond(linkType, base1, base2) {
    if (linkType === 'Bond') {
        return isUnsafeN(base1) && isUnsafeN(base2);
    }
    if (NN_ALERT_BOTH_SIDES.has(linkType)) {
        return isUnsafeN(base1) || isUnsafeN(base2);
    }
    if (NN_ALERT_BASE1_SIDE.has(linkType)) {
        return isUnsafeN(base1);
    }
    if (NN_ALERT_BASE2_SIDE.has(linkType)) {
        return isUnsafeN(base2);
    }
    return false;
}

/**
 * Find valid bond vector [attachment, terminal] atom pairs on a fragment for a given atom.
 * Only considers atoms removable with minimal disruption to protein interactions:
 * hydrogens, halogens, and XH3 / X(Hal)3 centers (methyl, CF3, SiH3, R-NH3+, etc.).
 * Respects fragment attachment atom annotations if present.
 * @param {Atom} atom - The candidate attachment atom on the fragment
 * @param {DecodedFragment} frag - The fragment being examined
 * @returns {[Atom, Atom][]} Array of [from, to] atom pairs representing possible bond vectors
 */
function findFragBondVectorAtomPairs(atom, frag) {
    const bondVectorAtomPairs = [];

    // This is now used to determine bond vectors for fragment bond points.  The goal is
    // not to identify all possible replaceable terminals, but ones that are less likely
    // to significantly change the interaction of the fragment with the protein.  Thus we
    // only include hydrogens, halogens, and carbons with 3 atoms of the same type
    // (methyl, CF3).  Oxygens, phosphates, etc. are excluded for this reason, even though
    // theoretically they could form a bond.  Carboxylates, nitros, etc. not considered
    // (turning into ester links). NOTE: This bond vector determination is different from
    // that for compounds, where replacements can be made at link atoms. Generally it is
    // only desired to link at hydrogens or halogens, so not all terminals are valid here.

    /**
     * Returns true if the atom is a valid terminal for bond vector replacement:
     * a hydrogen, halogen, or an XH3 / X(Hal)3 center (e.g. methyl, CF3, SiH3,
     * R-NH3+) where the three terminal substituents are rotationally equivalent.
     * @param {Atom} tatom
     * @returns {boolean}
     */
    function isTerminalAtom(tatom) {
        // assumes rings were found elsewhere
        if (tatom.ringOrdinals?.length || tatom.hasDoubleBond()) return false;
        return isHydrogen(tatom) || isHalogen(tatom) || atomHasEquivalentTerminals(tatom);
    }

    /**
     * @param {Atom} from
     * @param {Atom} to
     */
    function addBondPair(from, to) {
        bondVectorAtomPairs.push([from, to]);
        // ReportStatus("%s: Bond vector added from %s to %s.",
        // from.GetAtomGroup().GetName(), from.GetName(), to.GetName());
    }

    // Attachment atoms are space-separated atom names on baseFrag.attachmentAtoms,
    // decoded from the server's JSON fragment name (j.attachment_atoms).
    // Build a Set on first access to avoid repeated string splitting.
    /**
     * Return the Set of attachment atom names for a base fragment,
     * building it lazily on first access.
     * @param {object} baseFrag
     * @returns {Set<string>}
     */
    function getAttachmentAtomSet(baseFrag) {
        if (!baseFrag?.attachmentAtomSet) {
            const names = baseFrag?.attachmentAtoms;
            baseFrag.attachmentAtomSet = names
                ? new Set(names.split(' '))
                : new Set();
        }
        return baseFrag.attachmentAtomSet;
    }
    /** @param {DecodedFragment} f @returns {boolean} */
    function fragHasAttachmentAtoms(f) {
        return getAttachmentAtomSet(f.baseFrag).size > 0;
    }
    /** @param {DecodedFragment} f @param {Atom} a @returns {boolean} */
    function isAttachmentAtom(f, a) {
        return getAttachmentAtomSet(f.baseFrag).has(a.atom);
    }

    if (atom === null) return [];
    const atomIsTerminal = isTerminalAtom(atom);
    const hasAttachmentAtoms = fragHasAttachmentAtoms(frag);
    if (atomIsTerminal && (hasAttachmentAtoms && !isAttachmentAtom(frag, atom))) return [];
    const bondedAtoms = atom.bondedAtoms;
    if (bondedAtoms.length === 0) return []; // ion?
    for (const batom of bondedAtoms) {
        // Scenario 1 (atom is itself an XH3-like terminal center): only pair
        // with H/halogen children — never with the parent heavy atom on the
        // other side of the bond.
        if (atomIsTerminal && !isHydrogen(batom) && !isHalogen(batom)) continue;
        if (isTerminalAtom(batom) && (!hasAttachmentAtoms || isAttachmentAtom(frag, batom))) {
            addBondPair(atom, batom);
        }
    }

    return bondVectorAtomPairs;
}

/**
 * Filter fragment search results by removing poses that clash with the source compound.
 * Walks the compound graph away from the bond terminal to collect atoms for VDW clash checking.
 * Uses a 0.8 overlap factor on VDW radii to allow slight overlap that minimization can resolve.
 * @param {{ startAtom: Atom, startTerminal: Atom, endAtom: Atom,
 *   endTerminal: Atom|null, findType: string }[]} results
 * @returns {typeof results} Results with clashing fragment poses removed
 */
export function removeClashingFragmentResults(results) {
    if (results.length === 0) {
        return results;
    }
    // Look for clashes with all atoms starting with the startAtom, in a graph direction
    // away from the StartTerminal atom.
    const startAtom = results[0].startAtom;
    const startTerminal = results[0].startTerminal;

    const atomsForClashCheck = new Set();
    const fragAtomsForClashCheck = new Set();

    /**
     * Return the atom and all its directly bonded neighbors.
     * @param {Atom} baseAtom
     * @returns {Atom[]}
     */
    function collectAdjacent(baseAtom) {
        return [baseAtom, ...baseAtom.bondedAtoms];
    }

    /**
     * Recursively collect all atoms connected to tatom into the given Set.
     * @param {Atom} tatom
     * @param {Set<Atom>} collection
     */
    function collectConnected(tatom, collection) {
        collection.add(tatom);
        for (const tcatom of tatom.bondedAtoms) {
            if (!collection.has(tcatom)) collectConnected(tcatom, collection);
        }
    }

    atomsForClashCheck.add(startTerminal); // want to skip going down this branch, so pre-visit
    // Walk the graph of connected atoms for clash detection (the ones not to be
    // replaced), collecting them.
    collectConnected(startAtom, atomsForClashCheck);
    for (const atom of collectAdjacent(startAtom)) {
        atomsForClashCheck.delete(atom);
    }

    // VDW clash check matching bfd-server cmpd-modifier.elp:
    // clashDist = 0.8 * (rvdw1 + rvdw2), allowing modest overlap that could
    // be relaxed during minimization. startAtom uses tighter threshold (1.0 A²)
    // since it's the connection point.
    const vdwOverlapFactor = 0.8;
    const startAtomClashDistSq = 1.0;

    /**
     * Returns true if any fragment atom clashes with any compound atom.
     * Uses a tighter threshold (1.0 Å²) at the startAtom connection point,
     * and VDW-based thresholds elsewhere.
     * @param {Iterable<Atom>} compoundAtoms
     * @param {Iterable<Atom>} fragAtoms
     * @returns {boolean}
     */
    function checkClash(compoundAtoms, fragAtoms) {
        for (const fatom of fragAtoms) {
            const fRadius = getAtomRadius(fatom);
            if (!fRadius) continue;
            for (const catom of compoundAtoms) {
                const distSq = pointDistanceSquared(fatom, catom);
                let clashDistSq;
                if (catom === startAtom) {
                    clashDistSq = startAtomClashDistSq;
                } else {
                    const cRadius = getAtomRadius(catom);
                    if (!cRadius) continue;
                    const d = vdwOverlapFactor * (fRadius + cRadius);
                    clashDistSq = d * d;
                }
                if (distSq < clashDistSq) return true;
            }
        }
        return false;
    }

    const compoundAtomArray = [...atomsForClashCheck];
    const newResults = [];
    for (const result of results) {
        const { endAtom, endTerminal } = result;
        if (endAtom == null) continue;
        const frag = endAtom.fragment;
        if (!frag) continue;

        fragAtomsForClashCheck.clear();
        fragAtomsForClashCheck.add(endTerminal);
        collectConnected(endAtom, fragAtomsForClashCheck);
        for (const atom of collectAdjacent(endAtom)) {
            fragAtomsForClashCheck.delete(atom);
        }

        if (!checkClash(compoundAtomArray, fragAtomsForClashCheck)) {
            newResults.push(result);
        } else {
            console.log(`Removing clashing fragment ${getFullResidueId(endAtom)}`);
        }
    }

    return newResults;
}
